import Papa from 'papaparse';
import ExcelJS from 'exceljs';

import type {
  LocationImportField,
  LocationImportRow,
  LocationMatchSuggestion,
  LocationRawSheet,
  LocationSheetLayout,
} from '../api/locations';

// Shared client-side location-import primitives. Parallel to memberImport.ts
// but with a simpler shape: location lists are smaller, the canonical schema
// is just four fields (name, address, notes, color), and there's no
// guardian / role-axis translation step.
//
// CSV / XLSX parsing is identical in structure to members so the UI feels
// consistent. The transforms differ — see transformLocationRows below.

export const FIELDS: { value: LocationImportField; required?: boolean }[] = [
  { value: 'name', required: true },
  { value: 'address' },
  { value: 'notes' },
  { value: 'color' },
];

// FieldMapping: which source column feeds each canonical field. Empty /
// missing = field is not imported. Required fields ('name') are enforced by
// canImport before submit.
export type LocationFieldMapping = Partial<Record<LocationImportField, string>>;

export const PREVIEW_ROWS = 20;
export const SHEET_KEY = '__sheet';

export interface ParsedLocationFile {
  headers: string[];
  rows: Record<string, string>[];
  sheetNames: string[];
}

// suggestionsToMapping picks the highest-confidence column per field.
export function suggestionsToMapping(
  suggestions: LocationMatchSuggestion[],
): LocationFieldMapping {
  const byField: Record<string, { column: string; confidence: number }> = {};
  for (const s of suggestions) {
    if (!s.field) continue;
    const prev = byField[s.field];
    if (!prev || s.confidence > prev.confidence) {
      byField[s.field] = { column: s.column, confidence: s.confidence };
    }
  }
  const out: LocationFieldMapping = {};
  for (const [field, pick] of Object.entries(byField)) {
    out[field as LocationImportField] = pick.column;
  }
  return out;
}

// extractRawSheets dispatches on filename suffix; identical contract to the
// members helper so the UI parses Excel and CSV the same way.
export async function extractRawSheets(file: File): Promise<LocationRawSheet[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.tsv') || file.type === 'text/csv') {
    return extractRawSheetsFromCSV(file);
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return extractRawSheetsFromXLSX(file);
  }
  return extractRawSheetsFromCSV(file);
}

function extractRawSheetsFromCSV(file: File): Promise<LocationRawSheet[]> {
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: false,
      dynamicTyping: false,
      complete: (results) => {
        const rows = (results.data ?? []).map((r) =>
          r.map((c) => (c == null ? '' : String(c))),
        );
        while (rows.length > 0) {
          const last = rows[rows.length - 1]!;
          if (!last.every((c) => c.trim() === '')) break;
          rows.pop();
        }
        resolve([{ sheet_name: '', rows }]);
      },
      error: (err) => reject(err),
    });
  });
}

async function extractRawSheetsFromXLSX(file: File): Promise<LocationRawSheet[]> {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  if (wb.worksheets.length === 0) return [];

  const out: LocationRawSheet[] = [];
  for (const sheet of wb.worksheets) {
    const sheetName = (sheet.name ?? '').trim();
    let colCount = sheet.columnCount;
    for (let i = 1; i <= sheet.rowCount; i++) {
      const r = sheet.getRow(i);
      if (r.actualCellCount > colCount) colCount = r.actualCellCount;
    }
    if (colCount < 1) colCount = 1;

    const rows: string[][] = [];
    for (let i = 1; i <= sheet.rowCount; i++) {
      const row = sheet.getRow(i);
      const cells: string[] = [];
      for (let c = 1; c <= colCount; c++) {
        const v = row.getCell(c).value;
        cells.push(stringifyCellValue(v).trim());
      }
      rows.push(cells);
    }
    while (rows.length > 0) {
      const last = rows[rows.length - 1]!;
      if (!last.every((c) => c === '')) break;
      rows.pop();
    }
    out.push({ sheet_name: sheetName, rows });
  }
  return out;
}

function stringifyCellValue(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.text === 'string') return o.text;
    if (Array.isArray(o.richText)) {
      return (o.richText as Array<{ text?: string }>)
        .map((seg) => seg?.text ?? '')
        .join('');
    }
    if ('result' in o) return stringifyCellValue(o.result);
  }
  return String(v);
}

export function previewSheets(sheets: LocationRawSheet[]): LocationRawSheet[] {
  return sheets.map((s) => ({
    sheet_name: s.sheet_name,
    rows: s.rows.slice(0, PREVIEW_ROWS),
  }));
}

export function defaultLayouts(sheets: LocationRawSheet[]): LocationSheetLayout[] {
  return sheets.map((s) => ({
    sheet_name: s.sheet_name,
    header_row_index: 0,
    skip_rows_before_header: 0,
    skip_row_pattern: '',
  }));
}

// applyLayout walks each raw sheet using its layout to produce the keyed
// ParsedLocationFile. Identical shape to the members helper. Header rows can
// differ across sheets — the cross-sheet header union is rebuilt in
// first-seen order.
export function applyLayout(
  sheets: LocationRawSheet[],
  layouts: LocationSheetLayout[],
): ParsedLocationFile {
  const headerOrder: string[] = [];
  const headerSeen = new Set<string>();
  const allRows: Record<string, string>[] = [];
  const sheetNames: string[] = [];

  sheets.forEach((sheet, i) => {
    const layout: LocationSheetLayout =
      layouts[i] ?? {
        sheet_name: sheet.sheet_name,
        header_row_index: 0,
        skip_rows_before_header: 0,
        skip_row_pattern: '',
      };
    sheetNames.push(sheet.sheet_name);

    const hdrIdx = layout.header_row_index;
    if (hdrIdx < 0 || hdrIdx >= sheet.rows.length) return;

    const headerRow = sheet.rows[hdrIdx]!;
    const sheetHeaders: { col: number; name: string }[] = [];
    headerRow.forEach((cell, col) => {
      const h = cell.trim();
      if (!h) return;
      sheetHeaders.push({ col, name: h });
      if (!headerSeen.has(h)) {
        headerSeen.add(h);
        headerOrder.push(h);
      }
    });

    const skipPattern = (layout.skip_row_pattern ?? '').trim();

    for (let r = hdrIdx + 1; r < sheet.rows.length; r++) {
      const dataRow = sheet.rows[r]!;
      if (skipPattern && (dataRow[0] ?? '').startsWith(skipPattern)) continue;
      const obj: Record<string, string> = {};
      let hasContent = false;
      for (const { col, name } of sheetHeaders) {
        const v = (dataRow[col] ?? '').trim();
        obj[name] = v;
        if (v) hasContent = true;
      }
      if (hasContent) {
        obj[SHEET_KEY] = sheet.sheet_name;
        allRows.push(obj);
      }
    }
  });

  return { headers: headerOrder, rows: allRows, sheetNames };
}

// SplitOption mirrors the in-app LocationFormModal split selector — the
// venue is conceptually divided into this many equal zones. 1 means "no
// zones" (whole place). Names are generated by defaultZoneNamesForSplit.
export type SplitOption = 1 | 2 | 4 | 8;

// Canonical zone name set per split option. 1/4 uses the same "1A, 1B, 2A,
// 2B" convention the in-app form uses; 1/8 extends with C and D suffixes.
// Kept identical to LocationFormModal.defaultZoneNamesForSplit so a venue
// imported at 1/8 and a venue split at 1/8 inside the edit modal look the
// same on the activity grid.
export function defaultZoneNamesForSplit(option: SplitOption): string[] {
  switch (option) {
    case 1:
      return [];
    case 2:
      return ['A', 'B'];
    case 4:
      return ['1A', '1B', '2A', '2B'];
    case 8:
      return ['1A', '1B', '1C', '1D', '2A', '2B', '2C', '2D'];
  }
}

// PreparedLocationRow couples one parsed source row with the free-text
// address the SPA will send to Google Places. The Places-resolved structured
// fields live on the row after the resolution step (see resolveAddresses in
// LocationImportFlow.tsx); transformLocationRows pulls them out for the wire
// payload.
export interface ResolvedLocationRow {
  name: string;
  color?: string;
  notes?: string;
  // The free-text address the user typed, kept for display only. Not sent
  // to the server.
  rawAddress?: string;
  // Google Places result, OR all undefined when the user typed manually /
  // Places returned no hit (we still allow the row through as a name-only
  // location).
  place_id?: string;
  formatted_address?: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country_code?: string;
  latitude?: number | null;
  longitude?: number | null;
}

// transformLocationRows projects parsed source rows into the LocationImportRow
// wire shape. Address columns and Places results are merged into the wire
// row's structured fields; the raw address column is discarded (it's only
// useful for the preview UI's "Found:" indicator).
//
// splitOption applies the chosen default zone split to every row. 1 (the no-
// zones default) sends nothing in the zones array; larger splits attach the
// canonical name set so each imported location lands with its zones already
// created. Per-row overrides aren't supported yet — the user adjusts zones
// post-import via the location edit modal.
export function transformLocationRows(
  rows: ResolvedLocationRow[],
  splitOption: SplitOption = 1,
): LocationImportRow[] {
  const zoneNames = defaultZoneNamesForSplit(splitOption);
  return rows.map<LocationImportRow>((row) => {
    const out: LocationImportRow = { name: row.name };
    if (row.color) out.color = row.color;
    if (row.notes) out.notes = row.notes;
    if (row.place_id) out.place_id = row.place_id;
    if (row.formatted_address) out.formatted_address = row.formatted_address;
    if (row.street) out.street = row.street;
    if (row.postal_code) out.postal_code = row.postal_code;
    if (row.city) out.city = row.city;
    if (row.country_code) out.country_code = row.country_code;
    if (row.latitude != null) out.latitude = row.latitude;
    if (row.longitude != null) out.longitude = row.longitude;
    if (zoneNames.length > 0) out.zones = zoneNames;
    return out;
  });
}

// projectParsedRow takes one parsed sheet row and the user's mapping and
// returns the ResolvedLocationRow seed (name + free-text address + notes +
// color). The Places resolution happens in LocationImportFlow against this
// rawAddress.
export function projectParsedRow(
  row: Record<string, string>,
  mapping: LocationFieldMapping,
): ResolvedLocationRow {
  const get = (col: string | undefined) => (col ? (row[col] ?? '').trim() : '');
  const out: ResolvedLocationRow = { name: get(mapping.name) };
  const color = get(mapping.color);
  if (color) out.color = color;
  const notes = get(mapping.notes);
  if (notes) out.notes = notes;
  const rawAddress = get(mapping.address);
  if (rawAddress) out.rawAddress = rawAddress;
  return out;
}

export function canImport(mapping: LocationFieldMapping): boolean {
  return !!mapping.name;
}
